import React, { useState, useContext } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Phone, Mail, MapPin, Send, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { submitQuote } from '../services/api';
import { ContentContext } from '../App';

const serviceOptions = [
  { value: "residential", label: { en: "Residential Roofing", zh: "住宅屋顶" } },
  { value: "commercial", label: { en: "Commercial Roofing", zh: "商业屋顶" } },
  { value: "siding", label: { en: "Siding Services", zh: "壁板服务" } },
  { value: "repair", label: { en: "Roof Repair", zh: "屋顶维修" } },
  { value: "inspection", label: { en: "Roof Inspection", zh: "屋顶检查" } },
  { value: "emergency", label: { en: "Emergency Service", zh: "紧急服务" } }
];

const ContactSection = () => {
  const { language } = useLanguage();
  const content = useContext(ContentContext);
  const company = content?.company || {};

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await submitQuote(formData);
      setIsSubmitted(true);
      toast.success(language === 'en' ? 'Quote request submitted successfully!' : '报价请求已成功提交！');
      
      // Reset after 3 seconds
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({ name: '', phone: '', email: '', address: '', service: '', message: '' });
      }, 3000);
    } catch (error) {
      toast.error(language === 'en' ? 'Failed to submit request. Please try again.' : '提交失败，请重试。');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    { icon: MapPin, label: language === 'en' ? 'Our Address' : '公司地址', value: company.address },
    { icon: Phone, label: language === 'en' ? 'Call Us' : '联系电话', value: company.phone, href: `tel:${company.phone}` },
    { icon: Mail, label: language === 'en' ? 'Email Us' : '电子邮件', value: company.email, href: `mailto:${company.email}` }
  ];

  const t = {
    title: language === 'en' ? 'Get In Touch' : '联系我们',
    subtitle: language === 'en' ? 'Request a Free Quote Today' : '立即获取免费报价',
    form: {
      name: language === 'en' ? 'Full Name' : '姓名',
      phone: language === 'en' ? 'Phone Number' : '电话号码',
      email: language === 'en' ? 'Email Address' : '电子邮件',
      address: language === 'en' ? 'Property Address' : '房产地址',
      service: language === 'en' ? 'Service Needed' : '所需服务',
      message: language === 'en' ? 'Tell us about your project' : '描述您的项目',
      submit: language === 'en' ? 'Request Quote' : '获取报价'
    }
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-amber-600 font-semibold tracking-wide uppercase text-sm">
            {language === 'en' ? 'Contact Us' : '联系我们'}
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-2">
            {t.title}
          </h2>
          <p className="text-slate-600 mt-4 text-lg">
            {t.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-12">
          {/* Contact Info */}
          <div className="lg:col-span-2 space-y-8">
            {contactInfo.map((item, index) => (
              <div key={index} className="flex gap-4">
                <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <div className="text-sm text-slate-500 mb-1">{item.label}</div>
                  {item.href ? (
                    <a
                      href={item.href}
                      className="text-lg font-semibold text-slate-900 hover:text-amber-600 transition-colors"
                    >
                      {item.value}
                    </a>
                  ) : (
                    <div className="text-lg font-semibold text-slate-900">{item.value}</div>
                  )}
                </div>
              </div>
            ))}

            {/* Business Hours */}
            <div className="bg-slate-50 rounded-xl p-6">
              <h3 className="font-semibold text-slate-900 mb-4">
                {language === 'en' ? 'Business Hours' : '营业时间'}
              </h3>
              <div className="space-y-2 text-slate-600">
                <div className="flex justify-between">
                  <span>{language === 'en' ? 'Monday - Friday' : '周一 - 周五'}</span>
                  <span>8:00 AM - 6:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span>{language === 'en' ? 'Saturday' : '周六'}</span>
                  <span>9:00 AM - 4:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span>{language === 'en' ? 'Sunday' : '周日'}</span>
                  <span>{language === 'en' ? 'Closed' : '休息'}</span>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-slate-200">
                <div className="text-amber-600 font-semibold">
                  {language === 'en' ? '24/7 Emergency Service Available' : '全天候紧急服务'}
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-3">
            <div className="bg-slate-50 rounded-2xl p-8">
              {isSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-10 h-10 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">
                    {language === 'en' ? 'Thank You!' : '谢谢您！'}
                  </h3>
                  <p className="text-slate-600">
                    {language === 'en' 
                      ? 'We\'ll contact you within 24 hours.'
                      : '我们将在24小时内与您联系。'}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        {t.form.name} *
                      </label>
                      <Input
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="bg-white border-slate-200 focus:border-amber-500 focus:ring-amber-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        {t.form.phone} *
                      </label>
                      <Input
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="bg-white border-slate-200 focus:border-amber-500 focus:ring-amber-500"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        {t.form.email}
                      </label>
                      <Input
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="bg-white border-slate-200 focus:border-amber-500 focus:ring-amber-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        {t.form.service} *
                      </label>
                      <Select
                        value={formData.service}
                        onValueChange={(value) => setFormData({ ...formData, service: value })}
                      >
                        <SelectTrigger className="bg-white border-slate-200 focus:border-amber-500 focus:ring-amber-500">
                          <SelectValue placeholder={language === 'en' ? 'Select service' : '选择服务'} />
                        </SelectTrigger>
                        <SelectContent>
                          {serviceOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label[language]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t.form.address}
                    </label>
                    <Input
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      className="bg-white border-slate-200 focus:border-amber-500 focus:ring-amber-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t.form.message}
                    </label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      className="bg-white border-slate-200 focus:border-amber-500 focus:ring-amber-500"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold py-6 text-lg"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <div className="w-5 h-5 border-2 border-slate-900/30 border-t-slate-900 rounded-full animate-spin" />
                        {language === 'en' ? 'Submitting...' : '提交中...'}
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Send className="w-5 h-5" />
                        {t.form.submit}
                      </span>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
